import { useState, useEffect } from "react";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { useCachedBlobUrl } from "../../Services/useBlobDownloader";

const ImageGallery = ({
  message,
  isEditable = false,
  onImagesChange,
}) => {
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isLightboxOpen, setLightboxOpen] = useState(false);
  const { getCachedBlobUrl } = useCachedBlobUrl();

  const [visibleKeys, setVisibleKeys] = useState(
    Array.isArray(message?.images) ? message.images : []
  );

  useEffect(() => {
    if (Array.isArray(message?.images)) {
      setVisibleKeys(message.images);
    } else {
      setVisibleKeys([]);
    }
  }, [message?.id, message?.images]);

  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    if (!Array.isArray(visibleKeys) || visibleKeys.length === 0) {
      setGalleryImages([]);
      return;
    }

    const ac = new AbortController();

    (async () => {
      try {
        const results = await Promise.all(
          visibleKeys.map(async (item, i) => {
            const key = item.key ?? item;
            const url = item.url ?? null;

            let blobUrl;
            if (!url) {
              blobUrl = await getCachedBlobUrl(key, "image", { signal: ac.signal });
            } else {
              blobUrl = url;
            }

            return {
              id: `${message?.id ?? "msg"}-${i}`,
              src: blobUrl,
              caption: `Image ${i + 1}`,
              originalKey: key,
            };
          })
        );
        setGalleryImages(results);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error loading images:", err);
        }
      }
    })();

    return () => ac.abort();
  }, [message?.id, visibleKeys, getCachedBlobUrl]);

  if (galleryImages.length === 0) {
    return null;
  }

  const backendFlag = message?.gallery;
  const isGrid =
    backendFlag === "true"
      ? true
      : backendFlag === "false"
        ? false
        : galleryImages.length > 1;

  const chunk = (arr, n) =>
    arr.reduce(
      (rows, _, i) => (i % n ? rows : [...rows, arr.slice(i, i + n)]),
      []
    );

  const openLightbox = (idx) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  const handleDeleteClick = (idx, e) => {
    e.stopPropagation();
    if (!isEditable) return;

    setVisibleKeys((prevKeys) => {
      const nextKeys = [...prevKeys];
      nextKeys.splice(idx, 1);

      if (onImagesChange) {
        onImagesChange(message.id, nextKeys);
      }

      return nextKeys;
    });
  };

  return (
    <div className="media-container">
      {!isGrid && (
        <div
          className={`single-image-container ${isEditable ? "editable" : ""}`}
          onClick={() => openLightbox(0)}
          style={{ position: "relative" }}
        >
          <img
            src={galleryImages[0].src}
            alt="media"
            className="single-image"
          />

          {isEditable && (
            <button
              type="button"
              className="remove-file-button image-delete-btn"
              onClick={(e) => handleDeleteClick(0, e)}
              title="Delete image"
              aria-label="Delete image"
              style={{
                position: "absolute",
                top: "6px",
                right: "6px",
                background: "rgba(0,0,0,0.6)",
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                fontSize: "12px",
                lineHeight: 1,
                padding: "4px 6px",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          )}
        </div>
      )}

      {isGrid && (
        <div className="custom-gallery-container">
          {chunk(galleryImages, 3).map((row, rIdx) => (
            <div key={rIdx} className={`gallery-row gcol-${row.length}`}>
              {row.map((img, cIdx) => {
                const globalIdx = rIdx * 3 + cIdx;
                return (
                  <div
                    key={img.id}
                    className={`gallery-item ${isEditable ? "editable" : ""}`}
                    onClick={() => openLightbox(globalIdx)}
                    style={{ position: "relative" }}
                  >
                    <img
                      src={img.src}
                      alt="media"
                      className="gallery-thumb"
                    />

                    {isEditable && (
                      <button
                        type="button"
                        className="remove-file-button image-delete-btn"
                        onClick={(e) => handleDeleteClick(globalIdx, e)}
                        title="Delete image"
                        aria-label="Delete image"
                        style={{
                          position: "absolute",
                          top: "6px",
                          right: "6px",
                          background: "rgba(0,0,0,0.6)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "12px",
                          fontSize: "12px",
                          lineHeight: 1,
                          padding: "4px 6px",
                          cursor: "pointer",
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {isLightboxOpen && (
        <Lightbox
          mainSrc={galleryImages[lightboxIndex].src}
          nextSrc={
            galleryImages[(lightboxIndex + 1) % galleryImages.length].src
          }
          prevSrc={
            galleryImages[
              (lightboxIndex + galleryImages.length - 1) % galleryImages.length
            ].src
          }
          imageCaption={galleryImages[lightboxIndex].caption}
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={() =>
            setLightboxIndex(
              (lightboxIndex + galleryImages.length - 1) %
                galleryImages.length
            )
          }
          onMoveNextRequest={() =>
            setLightboxIndex(
              (lightboxIndex + 1) % galleryImages.length
            )
          }
        />
      )}
    </div>
  );
};

export default ImageGallery;
